import type { JwtPayload } from '@connectdevice/types';
import type { BatteryStatus } from '@connectdevice/types';

const API_BASE = '/api';

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    credentials: 'include', // Include HttpOnly cookies
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error((error as { error?: string }).error ?? `HTTP ${response.status}`);
  }

  return response.json() as Promise<T>;
}

// ─── Auth API ─────────────────────────────────────────────────────────────────

export interface LoginResponse {
  user: Pick<JwtPayload, 'sub' | 'username' | 'clientId' | 'role'>;
}

export async function login(username: string, password: string): Promise<LoginResponse> {
  return fetchJson<LoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

export async function logout(): Promise<void> {
  await fetchJson('/auth/logout', { method: 'POST' });
}

export async function getMe(): Promise<LoginResponse> {
  return fetchJson<LoginResponse>('/auth/me');
}

// ─── Battery Tests API ────────────────────────────────────────────────────────

export interface BatteryTestResult {
  id: number;
  serialNumber: string;
  clientId: number;
  equipmentId: string;
  measuredMah: number;
  internalResistance: number | null;
  configuredMah: number;
  status: BatteryStatus;
  description: string | null;
  createdAt: string;
}

export interface PaginatedTests {
  data: BatteryTestResult[];
  total: number;
  page: number;
  limit: number;
  hasNextPage: boolean;
}

export async function getBatteryTests(page = 1, limit = 20): Promise<PaginatedTests> {
  return fetchJson<PaginatedTests>(`/battery-tests?page=${page}&limit=${limit}`);
}

// ─── Battery Models API ───────────────────────────────────────────────────────

export interface BatteryModel {
  id: number;
  clientId: number;
  name: string;
  voltage: number;
  capacityMah: number;
  approvalThreshold: number;
  rejectionThreshold: number;
  description: string | null;
  createdAt: string;
}

export async function getBatteryModels(): Promise<{ data: BatteryModel[] }> {
  return fetchJson<{ data: BatteryModel[] }>('/battery-models');
}

export async function createBatteryModel(payload: Omit<BatteryModel, 'id' | 'clientId' | 'createdAt'>): Promise<{ data: BatteryModel }> {
  return fetchJson<{ data: BatteryModel }>('/battery-models', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
